import React from "react";
import { Clock, Settings } from "lucide-react";
import { CartTableProps } from "../../../../redux/cartConfigurationSlice";
import {
  formatTime,
  getPriorityColor,
  getPriorityText,
  getPrioritySortValue,
} from "../../../../dummyData/distributionPlan";
import "./index.css";

const CartTable: React.FC<CartTableProps> = ({
  carts,
  bulkEditMode,
  selectedCarts,
  setSelectedCarts,
  setEditingCart,
  sortBy,
  availableGates,
}) => {
  const sortedCarts = [...carts].sort((a, b) => {
    switch (sortBy) {
      case "priority":
        return (
          getPrioritySortValue(a.priority) - getPrioritySortValue(b.priority)
        );
      case "distance":
        return (b.distance || 0) - (a.distance || 0);
      case "leaveTime":
        return (
          new Date(a.leaveTime || Date.now()).getTime() -
          new Date(b.leaveTime || Date.now()).getTime()
        );
      case "cartId":
        return a.id.localeCompare(b.id);
      default:
        return 0;
    }
  });

  // Handle select all checkbox
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedCarts(new Set(carts.map((c: { id: string }) => c.id)));
    } else {
      setSelectedCarts(new Set());
    }
  };

  // Handle individual cart selection
  const handleCartSelection = (cartId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedCarts);
    if (isSelected) {
      newSelected.add(cartId);
    } else {
      newSelected.delete(cartId);
    }
    setSelectedCarts(newSelected);
  };

  // Check if all carts are selected
  const allSelected = carts.length > 0 && selectedCarts.size === carts.length;

  // Check if some carts are selected (for indeterminate state)
  const someSelected =
    selectedCarts.size > 0 && selectedCarts.size < carts.length;

  return (
    <div className="cart-configuration-table-container">
      <table className="cart-configuration-table">
        <thead>
          <tr>
            {bulkEditMode && (
              <th className="cart-configuration-table-header-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  className="cart-configuration-table-checkbox"
                />
              </th>
            )}
            <th>Cart Details</th>
            <th className="cart-configuration-table-header-center">Priority</th>
            <th className="cart-configuration-table-header-center">Distance</th>
            <th className="cart-configuration-table-header-center">
              Leave Time
            </th>
            <th className="cart-configuration-table-header-center">
              Assigned Gate
            </th>
            <th className="cart-configuration-table-header-center">
              Cart Type
            </th>
            <th className="cart-configuration-table-header-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedCarts.map((cart, index) => {
            const isSelected = selectedCarts.has(cart.id);
            const priorityClass = getPriorityColor(cart.priority);

            return (
              <tr
                key={cart.id}
                className={`cart-configuration-table-row ${
                  isSelected ? "selected" : ""
                }`}
              >
                {bulkEditMode && (
                  <td className="cart-configuration-table-cell-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) =>
                        handleCartSelection(cart.id, e.target.checked)
                      }
                      className="cart-configuration-table-checkbox"
                    />
                  </td>
                )}
                <td>
                  <div className="cart-configuration-table-details">
                    <div
                      className={`cart-configuration-table-priority-indicator ${priorityClass}`}
                    ></div>
                    <div>
                      <p className="cart-configuration-table-id">
                        {cart.route}
                      </p>
                      <p className="cart-configuration-table-rider">
                        {cart.rider}
                      </p>
                      <p className="cart-configuration-table-route">
                        {cart.id}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="cart-configuration-table-cell-center">
                  <span
                    className={`cart-configuration-table-priority-badge ${priorityClass}`}
                  >
                    {getPriorityText(cart.priority)}
                  </span>
                </td>
                <td className="cart-configuration-table-cell-center cart-configuration-table-distance-cell">
                  {(cart.distance || 0).toFixed(1)}km
                </td>
                <td className="cart-configuration-table-cell-center">
                  <div className="cart-configuration-table-leave-time">
                    <Clock className="cart-configuration-table-time-icon" />
                    {cart.leaveTime ? (
                      <span>{formatTime(cart.leaveTime)}</span>
                    ) : (
                      <span className="cart-configuration-table-no-gate">
                        Not Set
                      </span>
                    )}
                  </div>
                </td>

                <td className="cart-configuration-table-cell-center">
                  {cart.assignedGate ? (
                    <span className="cart-configuration-table-gate-badge">
                      {cart.assignedGate}
                    </span>
                  ) : (
                    <span className="cart-configuration-table-no-gate">
                      Not Assigned
                    </span>
                  )}
                </td>
                <td className="cart-configuration-table-cell-center">
                  {cart.cartType ? (
                    <span className="cart-configuration-table-cart-type">
                      {cart.cartType}
                    </span>
                  ) : (
                    <span className="cart-configuration-table-no-gate">
                      Not Set
                    </span>
                  )}
                </td>
                <td className="cart-configuration-table-cell-center">
                  <button
                    onClick={() => setEditingCart(cart)}
                    className="cart-configuration-table-configure-btn"
                    disabled={bulkEditMode}
                  >
                    <Settings className="cart-configuration-table-btn-icon" />
                    Configure
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Selection summary */}
      {bulkEditMode && selectedCarts.size > 0 && (
        <div className="cart-configuration-selection-summary">
          <span className="cart-configuration-selected-count">
            {selectedCarts.size} cart{selectedCarts.size !== 1 ? "s" : ""}{" "}
            selected
          </span>
        </div>
      )}
    </div>
  );
};

export default CartTable;
